import React, { useState } from 'react';
import { PDPClass, Participant } from '../../../../store/pdp/types';
import { ICompany } from '../../../../store/companies/companiesTypes';
import { convertNumbersToProperFormat } from '../../../../utils/number';
import { blue } from '../../../../utils/style/colors';
import Button from '../../../../components/Button';
import Icon from '../../../../components/Icon';
import Select from '../../../../components/Select';
import { MultiDatePicker_Range } from '../../../../components/MyDatePicker/MultiDatePicker';
import messages from './Messages';

interface DistrictAnalyticsProps {
  toShowClasses: PDPClass[];
  company: ICompany;
  isOpen: boolean;
}

const DistrictAnalytics: React.FC<DistrictAnalyticsProps> = ({
  toShowClasses,
  company,
  isOpen,
}) => {
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [dateRange, setDateRange] = useState<{
    startDate?: number;
    dueDate?: number;
  }>({});

  const roleOptions = Object.values(company.roles)
    .filter(
      (role) => role.roleType !== 'District' && role.roleType !== 'InProject'
    )
    .map((role) => ({
      value: role.id,
      label: role.displayName || role.name,
    }));

  const [collapsedDistricts, setCollapsedDistricts] = useState(() => {
    const districts = Object.values(company.roles).filter(
      (it) => it.roleType === 'District'
    );
    const toCollapse = new Set<number>();
    districts.forEach((d) => {
      if (
        d.surroundingMeRoles?.length &&
        d.surroundingMeRoles.some((parentId) =>
          districts.find(
            (parent) =>
              parent.id === parentId && !parent.surroundingMeRoles?.length
          )
        )
      ) {
        toCollapse.add(d.id);
      }
    });
    return toCollapse;
  });

  const getParticipantFinishDate = (
    participant: Participant,
    clazz: PDPClass
  ): number | null => {
    const group = participant.participantProps.group;
    const overrides = participant.participantProps.phasePropOverride || [];

    if (!group && clazz.properties.classGroups.length > 1) {
      console.warn(
        `Participant ${participant.userId} has no group selected in multi-group class`
      );
      return clazz.endDate || null;
    }

    const effectiveGroup =
      group ||
      (clazz.properties.classGroups.length === 1
        ? clazz.properties.classGroups[0].key
        : null);
    if (!effectiveGroup) return clazz.endDate || null;

    let maxEndDate = 0;

    clazz.properties.phaseProps.forEach((phase) => {
      if (phase.disabled) return;

      const override = overrides.find(
        (o) =>
          o.phaseTagId === phase.phaseTagId &&
          o.phaseStageId === phase.phaseStageId
      );

      const endDate =
        override?.endDate || phase.phaseEndDates?.[effectiveGroup];
      if (endDate && endDate > maxEndDate) {
        maxEndDate = endDate;
      }
    });

    return maxEndDate || clazz.endDate || null;
  };

  const getAnalytics = () => {
    if (!isOpen) return new Map();

    const districts = Object.values(company.roles).filter(
      (it) => it.roleType === 'District'
    );

    const districtMap = districts.reduce(
      (acc, d) => {
        acc[d.id] = { ...d, children: [], height: 0 };
        return acc;
      },
      {} as Record<number, any>
    );

    districts.forEach((d) => {
      if (d.surroundingMeRoles?.length) {
        d.surroundingMeRoles.forEach((parentId) => {
          if (districtMap[parentId]) {
            districtMap[parentId].children.push(d.id);
          }
        });
      }
    });

    const calculateDepth = (id: number, depth = 0): number => {
      districtMap[id].depth = depth;
      districtMap[id].children.forEach((childId: number) =>
        calculateDepth(childId, depth + 1)
      );
      return depth;
    };

    const preOrderSort = (id: number, result: any[] = []): any[] => {
      const district = districtMap[id];
      result.push({
        ...district,
        depth: district.depth,
        parentId: district.surroundingMeRoles?.[0],
      });
      district.children.forEach((childId: number) =>
        preOrderSort(childId, result)
      );
      return result;
    };

    const roots = districts.filter((d) => !d.surroundingMeRoles?.length);
    roots.forEach((root) => calculateDepth(root.id));
    const sortedDistricts = roots.flatMap((root) => preOrderSort(root.id));

    const districtAnalytics = new Map<
      string,
      {
        id: number;
        totalTrainingHour: number;
        totalPersonTrainingHour: number;
        totalBudget: number;
        allRelatedUsers: Set<number>;
        depth: number;
        parentId?: number;
        children: number[];
      }
    >();

    sortedDistricts.forEach((district) => {
      districtAnalytics.set(`d_${district.id}`, {
        id: district.id,
        totalTrainingHour: 0,
        totalPersonTrainingHour: 0,
        totalBudget: 0,
        allRelatedUsers: new Set<number>(),
        depth: district.depth,
        parentId: district.parentId,
        children: district.children,
      });
    });

    toShowClasses.forEach((c) => {
      const participants = Object.values(c.participants).filter(
        (it) => it.participantProps.status !== 'Dropped'
      );

      let filteredParticipants = participants;

      // Filter by roles
      if (selectedRoles.length > 0) {
        filteredParticipants = filteredParticipants.filter((participant) => {
          const companyMember = company.members[+participant.userId];
          return (
            companyMember?.roles &&
            Object.values(companyMember.roles).some((r) =>
              selectedRoles.includes(r.id)
            )
          );
        });
      }

      // Filter by date range
      if (dateRange.startDate || dateRange.dueDate) {
        filteredParticipants = filteredParticipants.filter((participant) => {
          const finishDate = getParticipantFinishDate(participant, c);
          if (!finishDate) return false;

          if (dateRange.startDate && finishDate < dateRange.startDate)
            return false;
          if (dateRange.dueDate && finishDate > dateRange.dueDate) return false;

          return true;
        });
      }

      const participantsByDistrict = filteredParticipants.reduce(
        (acc, participant) => {
          const companyMember = company.members[+participant.userId];
          if (companyMember?.roles) {
            Object.values(companyMember.roles).forEach((r) => {
              const role = company.roles[r.id];
              if (role?.roleType === 'District') {
                if (!acc[role.id]) acc[role.id] = [];
                acc[role.id].push(participant);
              }
            });
          }
          return acc;
        },
        {} as Record<number, Participant[]>
      );

      Object.entries(participantsByDistrict).forEach(
        ([districtId, districtParticipants]) => {
          const key = `d_${districtId}`;
          const analytics = districtAnalytics.get(key);
          if (analytics) {
            analytics.totalTrainingHour += c.properties.trainingHour || 0;
            analytics.totalPersonTrainingHour +=
              (c.properties.trainingHour || 0) *
              districtParticipants.filter(
                (p) => p.participantProps.present === 'Present'
              ).length;
            analytics.totalBudget +=
              ((c.properties.budget || 0) / participants.length) *
              districtParticipants.length;
            districtParticipants.forEach((p) =>
              analytics.allRelatedUsers.add(+p.userId)
            );
          }
        }
      );
    });

    return districtAnalytics;
  };

  const districtAnalytics = getAnalytics();

  if (!isOpen) return null;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: ' 0 10px ',
        }}
      >
        <h3>{messages.courseAnalytics}</h3>
        <div
          style={{
            display: 'flex',
            gap: '20px',
            marginBottom: '10px',
            padding: '10px',
            flex: 1,
            alignItems: 'center',
          }}
        >
          <div style={{ flex: 1 }}>
            <Select
              multiChoice={true}
              allowRemove={true}
              placeholder={messages.entireOrganization}
              label={messages.filterPost}
              options={roleOptions}
              values={selectedRoles.map((roleId) => ({
                value: roleId,
                label:
                  company.roles[roleId]?.displayName ||
                  company.roles[roleId]?.name ||
                  '',
              }))}
              onChangeValues={(values) => {
                setSelectedRoles(values.map((v) => Number(v.value)));
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <MultiDatePicker_Range
              isEditable={true}
              hasTime={false}
              id={'filterAnalyseDate'}
              label={messages.filterByEndDate}
              values={{
                startDate: dateRange.startDate,
                dueDate: dateRange.dueDate,
              }}
              setDate={(values) => {
                setDateRange({
                  startDate: values.startDate || undefined,
                  dueDate: values.dueDate || undefined,
                });
              }}
            />
          </div>
        </div>
        <Button
          onClick={async () => {
            const ExcelJS = await import('exceljs');
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('District Analytics');

            const headers = [
              'District',
              messages.totalBudget,
              messages.totalTrainingHour,
              messages.allRelatedUsers,
              messages.totalPersonTrainingHour,
              messages.hourPerRelatedUser,
              messages.costPerRelatedUser,
            ];
            worksheet.addRow(headers);

            Array.from(districtAnalytics.entries()).forEach(
              ([_, analytics]) => {
                const district = company.roles[analytics.id];
                const row = worksheet.addRow([
                  district?.name || '--',
                  analytics.totalBudget,
                  analytics.totalTrainingHour,
                  analytics.allRelatedUsers.size,
                  analytics.totalPersonTrainingHour,
                  analytics.allRelatedUsers.size
                    ? analytics.totalPersonTrainingHour /
                      analytics.allRelatedUsers.size
                    : 0,
                  analytics.allRelatedUsers.size
                    ? analytics.totalBudget / analytics.allRelatedUsers.size
                    : 0,
                ]);

                const grayLevel = Math.min(analytics.depth * 40, 100);
                const hexColor = (150 + grayLevel)
                  .toString(16)
                  .padStart(2, '0');
                const backgroundColor = hexColor + hexColor + hexColor;

                row.eachCell((cell) => {
                  cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF' + backgroundColor },
                  };
                });
              }
            );

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'district-analytics.xlsx';
            a.click();
            window.URL.revokeObjectURL(url);
          }}
        >
          <Icon type="download" /> {messages.excelExport}
        </Button>
      </div>
      <div style={{ maxHeight: '80vh', overflow: 'auto' }}>
        <table>
          <thead
            style={{
              position: 'sticky',
              top: '0',
              background: 'var(--exact-white)',
              zIndex: 2,
            }}
          >
            <tr>
              <th
                style={{
                  position: 'sticky',
                  right: 0,
                  background: 'var(--exact-white)',
                  zIndex: 1,
                }}
              >
                {messages.district}
                <Icon
                  type="info"
                  color={blue}
                  title={messages.districtDescription}
                  style={{ marginRight: '1rem' }}
                />
              </th>
              <th>
                {messages.totalBudget}
                <Icon
                  type="info"
                  color={blue}
                  title={messages.totalBudgetDistrictDescription}
                  style={{ marginRight: '1rem' }}
                />
              </th>
              <th>
                {messages.totalTrainingHour}
                <Icon
                  type="info"
                  color={blue}
                  title={messages.totalTrainingHourDistrictDescription}
                  style={{ marginRight: '1rem' }}
                />
              </th>
              <th>
                {messages.allRelatedUsers}
                <Icon
                  type="info"
                  color={blue}
                  title={messages.allRelatedUsersDistrictDescription}
                  style={{ marginRight: '1rem' }}
                />
              </th>
              <th>
                {messages.totalPersonTrainingHour}
                <Icon
                  type="info"
                  color={blue}
                  title={messages.totalPersonTrainingHourDistrictDescription}
                  style={{ marginRight: '1rem' }}
                />
              </th>
              <th>
                {messages.hourPerRelatedUser}
                <Icon
                  type="info"
                  color={blue}
                  title={messages.hourPerRelatedUserDistrictDescription}
                  style={{ marginRight: '1rem' }}
                />
              </th>
              <th>
                {messages.costPerRelatedUser}
                <Icon
                  type="info"
                  color={blue}
                  title={messages.costPerRelatedUserDistrictDescription}
                  style={{ marginRight: '1rem' }}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from(districtAnalytics.entries())
              .filter(([_, analytics]) => {
                if (!analytics.parentId) return true;
                let parent: number | undefined = analytics.parentId;
                while (parent) {
                  if (collapsedDistricts.has(parent)) return false;
                  const parentAnalytics = Array.from(
                    districtAnalytics.values()
                  ).find((a) => a.id === parent);
                  parent = parentAnalytics?.parentId;
                }
                return true;
              })
              .map(([key, analytics]) => {
                const district = company.roles[analytics.id];
                const grayLevel = Math.min(analytics.depth * 30, 180);
                const backgroundColor = `rgb(${255 - grayLevel}, ${255 - grayLevel}, ${255 - grayLevel})`;
                const hasChildren = analytics.children.length > 0;
                const isCollapsed = collapsedDistricts.has(analytics.id);
                return (
                  <tr
                    key={key}
                    style={{
                      backgroundColor,
                      position:
                        hasChildren && !isCollapsed ? 'sticky' : 'static',
                      top:
                        hasChildren && !isCollapsed
                          ? `${2 + analytics.depth * 2}rem`
                          : 'auto',
                      zIndex: hasChildren && !isCollapsed ? 2 : 'auto',
                    }}
                  >
                    <td
                      style={{
                        paddingRight: `${analytics.depth * 20 - 12 * (hasChildren ? 1 : 0)}px`,
                        position: 'sticky',
                        right: 0,
                        background: backgroundColor,
                        zIndex: 1,
                      }}
                    >
                      {
                        <span
                          style={{
                            cursor: 'pointer',
                            marginRight: '5px',
                          }}
                          onClick={() => {
                            setCollapsedDistricts((prev) => {
                              const newSet = new Set(prev);
                              if (isCollapsed) newSet.delete(analytics.id);
                              else newSet.add(analytics.id);
                              return newSet;
                            });
                          }}
                        >
                          {hasChildren ? (isCollapsed ? '◀' : '▼') : ''}
                        </span>
                      }
                      {district?.name || '--'}
                    </td>
                    <td>
                      {convertNumbersToProperFormat(analytics.totalBudget)}{' '}
                      {messages.currency}
                    </td>
                    <td>
                      {convertNumbersToProperFormat(
                        analytics.totalTrainingHour,
                        1
                      )}{' '}
                      {messages.hour}
                    </td>
                    <td>
                      {convertNumbersToProperFormat(
                        analytics.allRelatedUsers.size
                      )}{' '}
                      {messages.person}
                    </td>
                    <td>
                      {convertNumbersToProperFormat(
                        analytics.totalPersonTrainingHour
                      )}{' '}
                      {messages.personHour}
                    </td>
                    <td>
                      {analytics.allRelatedUsers.size
                        ? convertNumbersToProperFormat(
                            analytics.totalPersonTrainingHour /
                              analytics.allRelatedUsers.size
                          )
                        : '--'}{' '}
                      {messages.hour}
                    </td>
                    <td>
                      {analytics.allRelatedUsers.size
                        ? convertNumbersToProperFormat(
                            analytics.totalBudget /
                              analytics.allRelatedUsers.size
                          )
                        : '--'}{' '}
                      {messages.currency}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DistrictAnalytics;
